/**
 * One rule for "is this menu section actually being served right now?".
 *
 * Tuscan Cove's 21 brunch items were orderable until 19:45 on a Sunday because
 * ordering only ever asked whether the RESTAURANT was open (`pickupHours` in
 * convex/orderSettings.ts). Brunch service ends at 14:00, so a guest could pay
 * for eggs benedict five hours after the kitchen switched menus, and the ticket
 * printed anyway. Section-level service windows are a second, narrower gate on
 * top of the restaurant's hours: a section is orderable only when both agree.
 *
 * Everything here is pure and takes `at` as a parameter, so the 14:00 boundary
 * and both daylight-saving transitions can be exercised at frozen instants
 * without a Convex harness. Both callers use this module and nothing else:
 * the server rejection in `convex/orders.ts` (the authority — a client can post
 * any cart it likes) and the UI hiding in
 * `src/components/order/ordering-menu-surface.tsx` (the courtesy, so a guest
 * never builds a cart that will be refused). Two copies of this rule would
 * drift and the UI copy would be the one guests believe.
 *
 * No new dependency: `Intl.DateTimeFormat` only, matching
 * `convex/lib/dashboardWindow.ts` and `convex/lib/calendarKeys.ts`.
 */

import { PLATFORM_TIME_ZONE, isValidTimeOfDay } from "./calendarKeys";

/** Half-open `[open, close)` in 24-hour "HH:MM" wall-clock time. */
export type ServiceWindow = { open: string; close: string };

/**
 * `section -> weekday -> windows`, where weekday is `"0"` (Sunday) through
 * `"6"` (Saturday).
 *
 * The weekday keys deliberately match how `pickupHours`/`deliveryHours` are
 * keyed in convex/orderSettings.ts, so an admin editing service windows next to
 * pickup hours is not looking at two different numbering schemes. They are
 * strings because this shape round-trips through Convex `v.record`, which has
 * string keys.
 *
 * `section` is the menu section name as stored on the menu item, not a slug —
 * the caller looks up `windows[item.section]` directly.
 */
export type SectionServiceWindows = Record<string, Record<string, ServiceWindow[]>>;

const weekdayFormatters = new Map<string, Intl.DateTimeFormat>();

/** Cached: a formatter per cart line would dominate an order validation pass. */
function weekdayFormatter(timeZone: string): Intl.DateTimeFormat {
  const cached = weekdayFormatters.get(timeZone);
  if (cached) return cached;
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone,
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
    // h23 so midnight reads as 00 and not 24; without it a 00:00 open would
    // compare against an hour of 24 and never match.
    hourCycle: "h23",
  });
  weekdayFormatters.set(timeZone, formatter);
  return formatter;
}

const WEEKDAY_INDEX: Record<string, number> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};

const WEEKDAY_PLURAL = [
  "Sundays",
  "Mondays",
  "Tuesdays",
  "Wednesdays",
  "Thursdays",
  "Fridays",
  "Saturdays",
];

type LocalClock = { weekday: string; minutes: number };

/**
 * The weekday and minute-of-day a person standing in the restaurant reads off
 * the wall at `at`.
 *
 * This must come from the zone and not from UTC arithmetic: a Sunday 10:00
 * Pacific is 17:00Z under PDT but 18:00Z under PST, so any implementation that
 * compares UTC hours is correct for eight months of the year and wrong for
 * four. Same reason the day is read from the zone too — Sunday 18:00 Pacific is
 * already Monday in UTC, and a UTC weekday would let Sunday brunch leak into
 * Monday evening.
 */
function localClockAt(at: number, timeZone: string): LocalClock {
  const parts = weekdayFormatter(timeZone).formatToParts(new Date(at));
  const read = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((part) => part.type === type)?.value ?? "";
  return {
    weekday: String(WEEKDAY_INDEX[read("weekday")] ?? 0),
    minutes: Number(read("hour")) * 60 + Number(read("minute")),
  };
}

/** Minutes since local midnight, or `null` when the value is not "HH:MM". */
function minutesOfDay(value: string): number | null {
  if (typeof value !== "string" || !isValidTimeOfDay(value)) return null;
  const [hour, minute] = value.split(":").map(Number);
  return hour * 60 + minute;
}

/**
 * A window is usable only when both ends parse and `close` is strictly after
 * `open`.
 *
 * MALFORMED VALUES DO NOT THROW. An admin who types "2pm" into the close field
 * would otherwise take down the entire ordering path with a 500 on every cart,
 * for every section, for every tenant. Instead the bad window simply never
 * matches: a section whose only window is malformed reads as not currently
 * served, which is visibly wrong to staff and safely wrong to guests. Failing
 * OPEN here would be the dangerous direction — it would sell brunch at 8pm,
 * which is the bug this module exists to fix.
 *
 * A window that does not wrap past midnight is also required on purpose. No
 * service period at these restaurants crosses midnight, and an overnight window
 * expressed as `{ open: "22:00", close: "02:00" }` would need to match two
 * different weekday keys to be meaningful. Rather than half-support that, an
 * inverted window matches nothing. If late-night service is ever added, model
 * it as two windows on two days.
 */
function windowBounds(window: ServiceWindow): [number, number] | null {
  if (!window || typeof window !== "object") return null;
  const open = minutesOfDay(window.open);
  const close = minutesOfDay(window.close);
  if (open === null || close === null || close <= open) return null;
  return [open, close];
}

/**
 * True when `section` is being served at the instant `at`.
 *
 * Three cases, and the first one is the multi-tenant safety property:
 *
 * 1. **Nothing configured** — no `windows` map, no entry for `section`, or no
 *    `section` on the item at all: **served**. One host-aware build serves every
 *    restaurant, so a section nobody gated must never be gated. Chef Roma has no
 *    brunch windows and must not inherit Tuscan's; an item with no section (a
 *    drink, a modifier-only line) is not a service period and must not be
 *    silently blocked.
 * 2. **Configured, but nothing on this weekday** — a missing key or an empty
 *    array: **not served**. Someone deliberately listed the days brunch runs;
 *    the days they left out are the days it does not run. This is what makes
 *    "Sunday brunch" mean anything on a Wednesday.
 * 3. **Otherwise** served when the local wall clock falls in `[open, close)`
 *    for at least one window that day. The close is EXCLUSIVE: 14:00 is when
 *    brunch ends, so 13:59 is the last servable minute and 14:00 is not. An
 *    inclusive close would keep the section orderable for one minute past the
 *    menu change, which is exactly the boundary this module was written for.
 *
 * `timeZone` defaults to {@link PLATFORM_TIME_ZONE} (America/Los_Angeles) — the
 * same platform-wide Pacific assumption `todayDateKey()` and the dashboard
 * window already make. There is no per-location timezone field to read today;
 * the parameter exists so a location outside PT can be threaded through from
 * its own config in one place. Do NOT invent a schema field to feed it.
 */
export function isSectionServedAt(
  windows: SectionServiceWindows | undefined,
  section: string | undefined,
  at: number,
  timeZone: string = PLATFORM_TIME_ZONE,
): boolean {
  if (!windows || !section) return true;
  const byWeekday = windows[section];
  if (!byWeekday) return true;

  const { weekday, minutes } = localClockAt(at, timeZone);
  const today = byWeekday[weekday];
  if (!Array.isArray(today) || today.length === 0) return false;

  return today.some((window) => {
    const bounds = windowBounds(window);
    if (!bounds) return false;
    return minutes >= bounds[0] && minutes < bounds[1];
  });
}

/** "10:00" -> "10:00am", "14:00" -> "2:00pm", "00:00" -> "12:00am". */
function formatClockTime(minutes: number): string {
  const hour24 = Math.floor(minutes / 60);
  const minute = minutes % 60;
  const suffix = hour24 < 12 ? "am" : "pm";
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  return `${hour12}:${String(minute).padStart(2, "0")}${suffix}`;
}

/**
 * Guest-facing copy for a section's schedule, e.g. `"Sundays 10:00am-2:00pm"`.
 *
 * Used verbatim in two places that must agree word for word: the rejection
 * message when the server refuses an out-of-window item, and the badge on the
 * menu section. A guest who reads "Sundays 10:00am-2:00pm" on the menu and then
 * gets refused with different wording assumes the site is broken.
 *
 * Returns `""` when the section is not configured, has no days, or has only
 * malformed windows — callers should treat an empty string as "say nothing"
 * rather than rendering an empty badge. Days are listed Sunday-first regardless
 * of the key order in the record, so two admins entering the same schedule in a
 * different order produce identical copy.
 *
 * Deliberately hand-rolled and small: a date-formatting dependency inside the
 * Convex function bundle to print "2:00pm" is not worth the bundle weight.
 */
export function describeSectionWindows(
  windows: SectionServiceWindows | undefined,
  section: string,
): string {
  const byWeekday = windows?.[section];
  if (!byWeekday) return "";

  const phrases: string[] = [];
  for (let day = 0; day < 7; day += 1) {
    const today = byWeekday[String(day)];
    if (!Array.isArray(today) || today.length === 0) continue;

    const ranges = today
      .map(windowBounds)
      .filter((bounds): bounds is [number, number] => bounds !== null)
      .map(
        ([open, close]) =>
          `${formatClockTime(open)}-${formatClockTime(close)}`,
      );
    if (ranges.length === 0) continue;

    const joined =
      ranges.length === 1
        ? ranges[0]
        : `${ranges.slice(0, -1).join(", ")} and ${ranges[ranges.length - 1]}`;
    phrases.push(`${WEEKDAY_PLURAL[day]} ${joined}`);
  }

  return phrases.join(", ");
}
